import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { route } from 'preact-router';
import { api } from '../state/auth.js';
import Modal from '../components/shared/Modal.jsx';
import Button from '../components/shared/Button.jsx';

const INTERNAL_LINKS = [
  { label: 'Documenten',        path: '/documents' },
  { label: 'Beleidsbibliotheek', path: '/policies' },
  { label: 'Kwalificaties',     path: '/kwalificaties' },
];

const STATUS_CONFIG = {
  none:   { label: '—',       color: 'var(--color-text-muted)',   bg: 'var(--color-bg-secondary)', border: 'var(--color-border)' },
  green:  { label: 'Gereed',  color: '#166534',                   bg: '#dcfce7',                   border: '#86efac' },
  orange: { label: 'Deels',   color: '#92400e',                   bg: '#fef3c7',                   border: '#fcd34d' },
  red:    { label: 'Ontbreekt', color: '#991b1b',                 bg: '#fee2e2',                   border: '#fca5a5' },
};

function statusCounts(items) {
  const counts = { green: 0, orange: 0, red: 0, none: 0 };
  for (const item of items) counts[item.status] = (counts[item.status] || 0) + 1;
  return counts;
}

function ProgressBar({ items }) {
  const total = items.length;
  if (total === 0) return null;
  const counts = statusCounts(items);
  return (
    <div style="display:flex;height:6px;border-radius:3px;overflow:hidden;gap:1px;background:var(--color-border)">
      {['green','orange','red'].map(s => {
        const pct = (counts[s] / total) * 100;
        if (pct === 0) return null;
        return <div key={s} style={`flex:0 0 ${pct}%;background:${STATUS_CONFIG[s].border}`} />;
      })}
    </div>
  );
}

function StatusBadge({ status, size = 'md' }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.none;
  const pad = size === 'sm' ? '2px 8px' : '3px 10px';
  const fs = size === 'sm' ? '11px' : '12px';
  return (
    <span style={`display:inline-block;padding:${pad};border-radius:12px;font-size:${fs};font-weight:700;color:${cfg.color};background:${cfg.bg};border:1px solid ${cfg.border};white-space:nowrap`}>
      {cfg.label}
    </span>
  );
}

function DomainBlock({ domain, onEditItem }) {
  const [open, setOpen] = useState(false);
  const counts = statusCounts(domain.items);
  const done = counts.green;
  const total = domain.items.length;

  return (
    <div style="border:1px solid var(--color-border);border-radius:12px;overflow:hidden;background:var(--color-surface)">
      <button
        onClick={() => setOpen(o => !o)}
        style="width:100%;display:flex;align-items:center;gap:12px;padding:14px 18px;background:none;border:none;cursor:pointer;text-align:left"
      >
        <div style="flex:1;min-width:0">
          <div style="display:flex;align-items:center;gap:10px;margin-bottom:6px">
            <span style="font-size:14px;font-weight:700;color:var(--color-text)">{domain.name}</span>
            <span style="font-size:11px;color:var(--color-text-muted)">{done}/{total} gereed</span>
            {counts.red > 0 && <StatusBadge status="red" size="sm" />}
            {counts.orange > 0 && <StatusBadge status="orange" size="sm" />}
          </div>
          <ProgressBar items={domain.items} />
        </div>
        <span style={`font-size:11px;color:var(--color-text-muted);transition:transform .2s;display:inline-block;transform:rotate(${open ? 180 : 0}deg);flex-shrink:0`}>▼</span>
      </button>

      {open && (
        <div style="border-top:1px solid var(--color-border)">
          {domain.items.map((item, i) => (
            <div
              key={item.id}
              style={`display:flex;align-items:flex-start;gap:12px;padding:12px 18px;${i < domain.items.length - 1 ? 'border-bottom:1px solid var(--color-border)' : ''}`}
            >
              <div style="flex:1;min-width:0">
                <div style="font-size:13px;color:var(--color-text);line-height:1.4">
                  {item.description}
                  {item.is_optional === 1 && (
                    <span style="font-size:11px;color:var(--color-text-muted);margin-left:6px">(optioneel)</span>
                  )}
                </div>
                {item.doc_link && (
                  item.doc_link.startsWith('/')
                    ? <button
                        onClick={e => { e.stopPropagation(); route(item.doc_link); }}
                        style="font-size:11px;color:var(--color-primary);margin-top:3px;display:inline-block;background:none;border:none;cursor:pointer;padding:0;text-align:left"
                      >
                        🔗 {INTERNAL_LINKS.find(l => l.path === item.doc_link)?.label || item.doc_link}
                      </button>
                    : <a
                        href={item.doc_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style="font-size:11px;color:var(--color-primary);margin-top:3px;display:inline-block;text-decoration:none;word-break:break-all"
                        onClick={e => e.stopPropagation()}
                      >
                        🔗 {item.doc_link.length > 60 ? item.doc_link.slice(0, 57) + '…' : item.doc_link}
                      </a>
                )}
                {item.notes && (
                  <div style="font-size:11px;color:var(--color-text-muted);margin-top:3px;font-style:italic">{item.notes}</div>
                )}
              </div>
              <div style="display:flex;align-items:center;gap:8px;flex-shrink:0">
                <StatusBadge status={item.status} />
                <button
                  onClick={() => onEditItem(item)}
                  style="border:none;background:none;cursor:pointer;color:var(--color-text-muted);font-size:14px;padding:2px 4px;border-radius:4px;line-height:1"
                  title="Bewerken"
                >✏️</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CertificationView() {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [editStatus, setEditStatus] = useState('none');
  const [editNotes, setEditNotes] = useState('');
  const [editDocLink, setEditDocLink] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api('/api/certification')
      .then(setDomains)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const openEdit = (item) => {
    setEditItem(item);
    setEditStatus(item.status || 'none');
    setEditNotes(item.notes || '');
    setEditDocLink(item.doc_link || '');
  };

  const handleSave = async () => {
    if (!editItem) return;
    setSaving(true);
    try {
      await api(`/api/certification/items/${editItem.id}`, {
        method: 'PUT',
        body: JSON.stringify({ status: editStatus, notes: editNotes, doc_link: editDocLink }),
      });
      setDomains(prev => prev.map(d => ({
        ...d,
        items: d.items.map(it =>
          it.id === editItem.id
            ? { ...it, status: editStatus, notes: editNotes, doc_link: editDocLink }
            : it
        ),
      })));
      setEditItem(null);
    } catch {
      // intentionally empty
    } finally {
      setSaving(false);
    }
  };

  const allItems = domains.flatMap(d => d.items);
  const totalCounts = statusCounts(allItems);
  const total = allItems.length;

  return (
    <div>
      <div class="page-header" style="margin-bottom:8px">
        <h1 class="page-title">K&amp;P Certificering</h1>
      </div>
      <p style="font-size:13px;color:var(--color-text-muted);margin:0 0 20px">
        Stoplicht per bewijsstuk voor de Regionale Jeugdopleiding-audit. Klik op een eis om de status bij te werken.
      </p>

      {/* Summary bar */}
      {!loading && total > 0 && (
        <div style="display:flex;gap:10px;flex-wrap:wrap;margin-bottom:20px">
          {['green','orange','red','none'].map(s => (
            <div key={s} style={`display:flex;align-items:center;gap:6px;padding:8px 14px;border-radius:10px;background:${STATUS_CONFIG[s].bg};border:1px solid ${STATUS_CONFIG[s].border}`}>
              <span style={`font-size:20px;font-weight:800;color:${STATUS_CONFIG[s].color}`}>{totalCounts[s] || 0}</span>
              <span style={`font-size:12px;color:${STATUS_CONFIG[s].color};font-weight:600`}>{STATUS_CONFIG[s].label}</span>
            </div>
          ))}
          <div style="display:flex;align-items:center;gap:6px;padding:8px 14px;border-radius:10px;background:var(--color-bg-secondary);border:1px solid var(--color-border)">
            <span style="font-size:20px;font-weight:800;color:var(--color-text)">{total}</span>
            <span style="font-size:12px;color:var(--color-text-muted);font-weight:600">Totaal eisen</span>
          </div>
        </div>
      )}

      {loading ? (
        <div style="display:flex;flex-direction:column;gap:10px">
          {[1,2,3,4].map(i => (
            <div key={i} class="skeleton" style="height:56px;border-radius:12px" />
          ))}
        </div>
      ) : (
        <div style="display:flex;flex-direction:column;gap:10px">
          {domains.map(d => (
            <DomainBlock key={d.id} domain={d} onEditItem={openEdit} />
          ))}
        </div>
      )}

      {/* Edit modal */}
      <Modal
        isOpen={!!editItem}
        title={editItem?.description || ''}
        onClose={() => setEditItem(null)}
      >
        <div style="display:flex;flex-direction:column;gap:16px">
          {/* Status knoppen */}
          <div>
            <div style="font-size:12px;font-weight:600;color:var(--color-text-muted);margin-bottom:8px;text-transform:uppercase;letter-spacing:.05em">Status</div>
            <div style="display:flex;gap:8px;flex-wrap:wrap">
              {Object.entries(STATUS_CONFIG).filter(([k]) => k !== 'none').map(([key, cfg]) => (
                <button
                  key={key}
                  onClick={() => setEditStatus(key)}
                  style={`padding:8px 16px;border-radius:8px;border:2px solid ${editStatus === key ? cfg.border : 'var(--color-border)'};background:${editStatus === key ? cfg.bg : 'transparent'};color:${editStatus === key ? cfg.color : 'var(--color-text-muted)'};font-weight:700;font-size:13px;cursor:pointer;transition:all .15s`}
                >
                  {cfg.label}
                </button>
              ))}
              <button
                onClick={() => setEditStatus('none')}
                style={`padding:8px 16px;border-radius:8px;border:2px solid ${editStatus === 'none' ? 'var(--color-primary)' : 'var(--color-border)'};background:${editStatus === 'none' ? 'var(--color-primary-light,#ede9fe)' : 'transparent'};color:${editStatus === 'none' ? 'var(--color-primary)' : 'var(--color-text-muted)'};font-weight:700;font-size:13px;cursor:pointer;transition:all .15s`}
              >
                Wissen
              </button>
            </div>
          </div>

          {/* Document link */}
          <div>
            <label style="display:block;font-size:12px;font-weight:600;color:var(--color-text-muted);margin-bottom:4px;text-transform:uppercase;letter-spacing:.05em">
              Link naar bewijs
            </label>
            <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:6px">
              {INTERNAL_LINKS.map(l => (
                <button
                  key={l.path}
                  type="button"
                  onClick={() => setEditDocLink(l.path)}
                  style={`padding:4px 10px;border-radius:8px;font-size:11px;font-weight:600;cursor:pointer;border:1px solid ${editDocLink === l.path ? 'var(--color-primary)' : 'var(--color-border)'};background:${editDocLink === l.path ? 'var(--color-primary-light,#ede9fe)' : 'var(--color-bg-secondary)'};color:${editDocLink === l.path ? 'var(--color-primary)' : 'var(--color-text-muted)'}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <input
              class="form-input"
              type="text"
              placeholder="https://sharepoint.com/… of /documents"
              value={editDocLink}
              onInput={e => setEditDocLink(e.target.value)}
            />
            <p style="font-size:11px;color:var(--color-text-muted);margin-top:4px">Klik een snelkoppeling of typ een externe URL (SharePoint, UCAD).</p>
          </div>

          {/* Notitie */}
          <div>
            <label style="display:block;font-size:12px;font-weight:600;color:var(--color-text-muted);margin-bottom:4px;text-transform:uppercase;letter-spacing:.05em">
              Notitie / actie
            </label>
            <textarea
              class="form-input"
              rows={3}
              placeholder="Wie pakt dit op? Wat moet er nog gedaan worden?"
              value={editNotes}
              onInput={e => setEditNotes(e.target.value)}
              style="resize:vertical"
            />
          </div>
        </div>

        <div class="flex gap-2 justify-end mt-4">
          <Button variant="secondary" onClick={() => setEditItem(null)}>Annuleren</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Opslaan…' : 'Opslaan'}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
